// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useTranslation } from 'react-i18next';
import FaceBioIcon from '../../../assets/icons/authenticators/face_bio.svg?react';
import IidIcon from '../../../assets/icons/authenticators/imprivata_ID.svg?react';
import PasswordIcon from '../../../assets/icons/authenticators/password.svg?react';
import PinIcon from '../../../assets/icons/authenticators/pin.svg?react';
import './Security.less';

const SECURITY = 'portal.security';
const METHOD_NAME = 'self-service.authenticator-list.auth-method-info';

const METHOD_ICONS = [
  { key: 'face', Icon: FaceBioIcon, nameKey: `${METHOD_NAME}.face-recognition` },
  { key: 'iid', Icon: IidIcon, nameKey: `${METHOD_NAME}.imprivata-id` },
  { key: 'password', Icon: PasswordIcon, nameKey: `${METHOD_NAME}.password` },
  { key: 'pin', Icon: PinIcon, nameKey: `${METHOD_NAME}.pin` },
] as const;
type MethodIcon = (typeof METHOD_ICONS)[number]['key'];

// Strongest first; `pale` marks the authenticators a level does not use.
const METHODS: readonly {
  factor: 'passwordless' | 'MFA' | 'single';
  pale: readonly MethodIcon[];
}[] = [
  { factor: 'passwordless', pale: ['password'] },
  { factor: 'MFA', pale: [] },
  { factor: 'single', pale: ['face', 'iid', 'pin'] },
];

export const AuthMethodsPanel = () => {
  const { t, i18n } = useTranslation();
  const listFormat = new Intl.ListFormat(i18n.language, { type: 'conjunction' });

  return (
    <div className="authentication-methods">
      <h2 className="authentication-methods-title">{t(`${SECURITY}.methods.title`)}</h2>
      <div className="authentication-methods-content">
        <div className="scale">
          <span>{t(`${SECURITY}.guidelines.scale-top`)}</span>
          <span>{t(`${SECURITY}.guidelines.scale-bottom`)}</span>
        </div>
        <div>
          {METHODS.map(({ factor, pale }) => (
            <div key={factor} className="method">
              <div className="method-title">
                <span>{t(`${SECURITY}.guidelines.factors.${factor}.title`)}</span>
                <span
                  role="img"
                  aria-label={listFormat.format(
                    METHOD_ICONS.filter(({ key }) => !pale.includes(key)).map(({ nameKey }) =>
                      t(nameKey),
                    ),
                  )}
                >
                  {METHOD_ICONS.map(({ key, Icon }) => (
                    <Icon key={key} className={pale.includes(key) ? 'pale' : undefined} />
                  ))}
                </span>
              </div>
              <p>{t(`${SECURITY}.guidelines.factors.${factor}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
